// getVipLevelLivestreamChatStyle.js
'use strict';
import { Collection } from 'immutable';
import createCachedSelector from '../resource/createCachedSelector.js';
import {
  getVipBenefitLevelThresholdByUserLevel,
  getUserLevelBenefitThresholdList,
} from '../selector/getUserLevelBenefitData.js';

/**
 * Select the user entered notice and chat log styles corresponding to the current user's VIP level
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {number} level - The user level to select the corresponding VIP level livestream chat style.
 * @param {number} minRequiredVipBenefitLevel - The minimum required VIP benefit level.
 * @param {number} minRequiredVipBenefitLevelIndex - The index of the minimum required VIP benefit level.
 * @param {Array} vipLevelLivestreamStyleList - The list of VIP level livestream styles.
 * @return {object} An object containing vipEnteredNoticeStyle and vipMessageLogStyle for the specified user level.
 */
const getVipLevelLivestreamChatStyle = createCachedSelector(
  (state, level) => {
    const { vipBenefitLevel } = getVipBenefitLevelThresholdByUserLevel(
      state,
      level
    );
    return vipBenefitLevel;
  },
  (state, level, minRequiredVipBenefitLevel) => minRequiredVipBenefitLevel,
  (state, level, minRequiredVipBenefitLevel, minRequiredVipBenefitLevelIndex) =>
    minRequiredVipBenefitLevelIndex,
  (
    state,
    level,
    minRequiredVipBenefitLevel,
    minRequiredVipBenefitLevelIndex,
    vipLevelLivestreamStyleList
  ) => vipLevelLivestreamStyleList,
  state => getUserLevelBenefitThresholdList(state),
  (
    level,
    minRequiredVipBenefitLevel,
    minRequiredVipBenefitLevelIndex,
    vipLevelLivestreamStyleList,
    userLevelBenefitThresholdList
  ) => {
    const result = {
      vipMessageLogStyle: {},
      vipEnteredNoticeStyle: {},
    };

    const maxLevelIndex = userLevelBenefitThresholdList.at(-1)?.level ?? 0;
    const vipLevelLivestreamList = vipLevelLivestreamStyleList.slice(
      minRequiredVipBenefitLevelIndex,
      maxLevelIndex
    );

    // Result shape:
    // {
    //   vipEnteredNoticeStyle: {
    //     3: ['border:#000', 'background:#505050' ],
    //     4: ['border:#000', 'background:#505050' ],
    //     ...
    //   },
    //   vipMessageLogStyle: {
    //     3: ['border:#000', 'background:#505050' ],
    //     4: ['border:#000', 'background:#505050' ],
    //     ...
    //   }
    // }
    const { vipEnteredNoticeStyle, vipMessageLogStyle } =
      vipLevelLivestreamList.reduce(
        (
          prev,
          [vipLevelLivestreamChatStyle, vipLevelLivestreamChatLogStyle],
          index
        ) => {
          const level = index + minRequiredVipBenefitLevel;
          prev.vipEnteredNoticeStyle[level] = vipLevelLivestreamChatStyle;
          prev.vipMessageLogStyle[level] = vipLevelLivestreamChatLogStyle;
          return prev;
        },
        {
          vipEnteredNoticeStyle: {},
          vipMessageLogStyle: {},
        }
      );

    if (Object.keys(vipEnteredNoticeStyle).includes(`${level}`)) {
      const [backgroundColor, borderColor] = vipEnteredNoticeStyle[level];

      result.vipEnteredNoticeStyle = {
        backgroundColor: backgroundColor?.split(':')?.[1],
        borderColor: borderColor?.split(':')?.[1],
      };
    }
    if (Object.keys(vipMessageLogStyle).includes(`${level}`)) {
      const [backgroundColor, borderColor] = vipMessageLogStyle[level];
      result.vipMessageLogStyle = {
        backgroundColor: backgroundColor?.split(':')?.[1],
        borderColor: borderColor?.split(':')?.[1],
      };
    }

    return result instanceof Collection ? result.toJS() : result;
  }
)(
  (
    state,
    level,
    minRequiredVipBenefitLevel,
    minRequiredVipBenefitLevelIndex,
    vipLevelLivestreamStyleList
  ) =>
    `${level}:${minRequiredVipBenefitLevelIndex}:${minRequiredVipBenefitLevel}:${vipLevelLivestreamStyleList?.join('') ?? ''}`
);

export default getVipLevelLivestreamChatStyle;
